// ============================================
// wireBackfill — binary decoder for the chunked swap backfill.
// ============================================
// WHY THIS EXISTS
// The backfill payload is a rectangular table: four columns, N rows, fixed
// types, no nesting anywhere. JSON is a TREE format, so shipping it as an
// array of objects re-transmits the four key names once per row — measured at
// 52% of a 20k-trade chunk being scaffolding rather than data, 0.70 MB of it
// the same 35 bytes repeated 20,000 times.
//
// Worse, the cost is paid at both ends for nothing: TradeBuffer is ALREADY
// columnar typed arrays (_time/_mcap/_amount Float64, _side Int8), so the old
// pipeline ran columns → tree → 20k heap objects → back into columns →
// discard. Two conversions that cancel out.
//
// The real motivation is server-side. `json.dumps` is synchronous and sits on
// the asyncio event loop: 29ms per chunk, up to 0.73s of BLOCKED loop for one
// heavy subscribe at SWAP_BACKFILL_MAX_ROWS. That is the same class of
// violation as synchronous disk I/O on the hot path — during it, every other
// client's swap frames, HL book updates and candle timers queue behind one
// person hitting refresh. Encoding to bytes drops it to ~4ms.
//
// Measured, 20k-trade chunk:
//                       encode(server)   payload   decode(client)
//   objects (JSON)          32.4 ms      2.04 MB      29.1 ms
//   binary columns           5.2 ms      0.40 MB      10.2 ms
//
// ── WIRE FORMAT: 'CHDT' v1 ──
// All little-endian. Header is a fixed 32 bytes, and every subsequent block is
// padded so its typed-array view is naturally aligned — the f64 columns come
// FIRST for exactly that reason, which lets the decoder build zero-copy views
// straight onto the received ArrayBuffer with no intermediate copies.
//
//   off  0   u32   magic     0x54444843  ('C','H','D','T')
//   off  4   u16   version   1
//   off  6   u16   kind      1 = backfill
//   off  8   u32   seq       chunk index, 0 = newest
//   off 12   u32   flags     bit0 = more chunks follow
//   off 16   u32   n         trade count
//   off 20   u16   tokenLen  utf8 byte length of the mint
//   off 22   u16   reserved  0
//   off 24   f64   t0        base timestamp, ms epoch
//   off 32   u8[tokenLen]    mint, utf8   → then zero-pad to a multiple of 8
//            f64[n]          amount
//            f64[n]          tradeMcap
//            i32[n]          dt, ms from t0
//            u8[ceil(n/8)]   side bitmask, bit SET = buy
//
// t0 is f64 rather than i64: ms epoch is ~1.75e12, exact well past 2^53, and
// it avoids BigInt on the hot path entirely.
//
// dt is i32 — ±24 days of range against a 6h backfill window. The ENCODER is
// responsible for verifying the span fits and falling back to JSON if it ever
// doesn't; a decoder cannot detect the overflow after the fact.
//
// PRECISION: amount and tradeMcap are f64, deliberately not f32. stepIdxOf is
// floor(mcap / bucket), and f32 carries ~0.003 absolute error at mcap 45,000 —
// harmless at bucket 100, but several price rows off at sub-1 buckets. Same
// reason TradeBuffer keeps _amount at Float64: values must stay bit-identical
// to the object path or dedup keys diverge.

export const WIRE_MAGIC = 0x54444843
export const WIRE_VERSION = 1
export const WIRE_KIND_BACKFILL = 1
const HEADER_BYTES = 32

/**
 * True when a received frame looks like one of ours. Cheap enough to run on
 * every binary frame — checks length and magic only.
 */
export function isWireFrame(buf) {
  if (!(buf instanceof ArrayBuffer) || buf.byteLength < HEADER_BYTES) return false
  return new DataView(buf).getUint32(0, true) === WIRE_MAGIC
}

/**
 * Decode a backfill frame into the SAME shape the JSON path produced:
 * { type, token, seq, more, trades: [{time, side, amount, tradeMcap}] }
 *
 * Materializing objects here is a deliberate choice. Handing columns to the
 * merge below would save a further ~8.6ms/chunk, but the merge, the spike
 * detector and the dedup path all iterate objects, and rewriting them buys
 * client time that was never the bottleneck — Layout alone is 365ms. The
 * boundary stays object-shaped so NOTHING downstream changes; the win that
 * motivated this is the server's event loop, and that is already banked by
 * the time these bytes leave the droplet.
 *
 * Returns null on any malformed frame so the caller can ignore it rather than
 * throw inside onmessage and take the socket handler down.
 */
export function decodeBackfillFrame(buf) {
  try {
    if (!isWireFrame(buf)) return null
    const dv = new DataView(buf)
    if (dv.getUint16(4, true) !== WIRE_VERSION) return null
    if (dv.getUint16(6, true) !== WIRE_KIND_BACKFILL) return null

    const seq      = dv.getUint32(8, true)
    const more     = (dv.getUint32(12, true) & 1) === 1
    const n        = dv.getUint32(16, true)
    const tokenLen = dv.getUint16(20, true)
    const t0       = dv.getFloat64(24, true)

    let o = HEADER_BYTES
    const token = new TextDecoder().decode(new Uint8Array(buf, o, tokenLen))
    o += tokenLen
    o = (o + 7) & ~7                       // pad to 8 for the f64 columns

    const need = o + n * 8 + n * 8 + n * 4 + ((n + 7) >> 3)
    if (buf.byteLength < need) return null  // truncated — refuse, don't guess

    // Zero-copy views. The padding above is what makes these legal: a
    // Float64Array view demands an 8-aligned byteOffset or it throws.
    const amounts = new Float64Array(buf, o, n); o += n * 8
    const mcaps   = new Float64Array(buf, o, n); o += n * 8
    const dts     = new Int32Array(buf, o, n);   o += n * 4
    const sides   = new Uint8Array(buf, o, (n + 7) >> 3)

    const trades = new Array(n)
    for (let i = 0; i < n; i++) {
      trades[i] = {
        time:      t0 + dts[i],
        side:      (sides[i >> 3] >> (i & 7)) & 1 ? 'buy' : 'sell',
        amount:    amounts[i],
        tradeMcap: mcaps[i],
      }
    }
    return { type: 'backfill', token, seq, more, trades }
  } catch {
    return null
  }
}
